import React from "react";
import Link from "next/link";

/**
 * Combined pattern matching: **bold**, *italic*, `code`, [label](url), plain URL
 * Capture groups: [2]=boldText, [3]=italicText, [4]=codeText, [5]=linkLabel, [6]=linkUrl, [7]=plainUrl
 *
 * Explicit `[label](…)` links also accept site-relative paths (`/datenschutz`),
 * which is what agent copy linking to our own pages uses. Bare URLs stay
 * absolute-only so prose containing a slash is not turned into a link.
 */
const INLINE_PATTERN =
  /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|\[(.+?)\]\(((?:https?:\/\/|\/)[^)\s]+)\)|(https?:\/\/[^\s)>]+))/g;

export function parseInlineMarkdown(text: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = INLINE_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    const fullMatch = match[0];
    const boldText = match[2];
    const italicText = match[3];
    const codeText = match[4];
    const linkLabel = match[5];
    const linkUrl = match[6];
    const plainUrl = match[7];

    if (boldText) {
      result.push(<strong key={match.index}>{boldText}</strong>);
    } else if (italicText) {
      result.push(<em key={match.index}>{italicText}</em>);
    } else if (codeText) {
      result.push(
        <code
          key={match.index}
          className="rounded bg-black/10 px-1 py-0.5 text-sm"
        >
          {codeText}
        </code>
      );
    } else if (linkLabel && linkUrl) {
      result.push(
        <Link
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {linkLabel}
        </Link>
      );
    } else if (plainUrl) {
      result.push(
        <Link
          key={match.index}
          href={plainUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {plainUrl}
        </Link>
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

function parseParagraph(text: string, keyPrefix: string): React.ReactNode[] {
  const lines = text.split("\n");
  return lines.flatMap((line, j) => [
    ...parseInlineMarkdown(line),
    ...(j < lines.length - 1 ? [<br key={`${keyPrefix}-br-${j}`} />] : []),
  ]);
}

const UNORDERED_ITEM = /^[-*]\s+(.+)/;
const ORDERED_ITEM = /^\d+\.\s+(.+)/;
const LIST_LINE = /^([-*]|\d+\.)\s+/;

function isList(text: string): boolean {
  const lines = text.split("\n").filter((l) => l.trim());
  return lines.length > 0 && lines.every((l) => LIST_LINE.test(l.trim()));
}

function parseList(text: string, keyPrefix: string): React.ReactNode {
  const lines = text.split("\n").filter((l) => l.trim());
  const isOrdered = ORDERED_ITEM.test(lines[0].trim());
  const Tag = isOrdered ? "ol" : "ul";
  const className = isOrdered
    ? "my-2 list-decimal pl-5 space-y-1"
    : "my-2 list-disc pl-5 space-y-1";
  const pattern = isOrdered ? ORDERED_ITEM : UNORDERED_ITEM;

  return (
    <Tag key={`${keyPrefix}-list`} className={className}>
      {lines.map((line, i) => {
        const match = line.trim().match(pattern);
        const content = match ? match[1] : line.trim();
        return (
          <li key={`${keyPrefix}-li-${i}`}>{parseInlineMarkdown(content)}</li>
        );
      })}
    </Tag>
  );
}

const SEPARATOR_ROW = /^\|?[\s-:|]+\|[\s-:|]*$/;

function isTable(text: string): boolean {
  const lines = text.split("\n").filter((l) => l.trim());
  return (
    lines.length >= 2 &&
    lines.every((l) => l.trim().includes("|")) &&
    SEPARATOR_ROW.test(lines[1])
  );
}

function parseTableRow(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function parseTable(text: string, keyPrefix: string): React.ReactNode {
  const lines = text.split("\n").filter((l) => l.trim());
  const headerCells = parseTableRow(lines[0]);
  const bodyRows = lines.slice(2);

  return (
    <div
      key={`${keyPrefix}-table-wrapper`}
      className="my-2 overflow-x-auto rounded-xl bg-black/5"
    >
      <table key={`${keyPrefix}-table`} className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-black/10">
            {headerCells.map((cell, j) => (
              <th key={j} className="px-3 py-2 font-semibold">
                {parseInlineMarkdown(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, i) => {
            const cells = parseTableRow(row);
            return (
              <tr key={i} className="border-b border-black/5 last:border-0">
                {cells.map((cell, j) => (
                  <td key={j} className="px-3 py-2">
                    {parseInlineMarkdown(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function renderTextBlocks(text: string, keyPrefix: string): React.ReactNode[] {
  const paragraphs = text.split(/\n\n+/);

  if (paragraphs.length === 1) {
    if (isTable(paragraphs[0])) {
      return [parseTable(paragraphs[0], `${keyPrefix}-0`)];
    }
    if (isList(paragraphs[0])) {
      return [parseList(paragraphs[0], `${keyPrefix}-0`)];
    }
    return parseParagraph(paragraphs[0], `${keyPrefix}-0`);
  }

  return paragraphs.map((paragraph, i) => {
    if (isTable(paragraph)) {
      return parseTable(paragraph, `${keyPrefix}-${i}`);
    }
    if (isList(paragraph)) {
      return parseList(paragraph, `${keyPrefix}-${i}`);
    }
    return (
      <p key={`${keyPrefix}-${i}`}>
        {parseParagraph(paragraph, `${keyPrefix}-${i}`)}
      </p>
    );
  });
}

export function parseMarkdown(content: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const codeBlockPattern = /```(\w*)\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = codeBlockPattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index).trim();
      if (textBefore) {
        parts.push(...renderTextBlocks(textBefore, `pre-${match.index}`));
      }
    }

    parts.push(
      <pre
        key={`code-${match.index}`}
        className="my-2 overflow-x-auto rounded-xl bg-black/10 p-3 text-sm"
      >
        <code>{match[2]}</code>
      </pre>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex === 0) {
    return renderTextBlocks(content, "root");
  }

  if (lastIndex < content.length) {
    const textAfter = content.slice(lastIndex).trim();
    if (textAfter) {
      parts.push(...renderTextBlocks(textAfter, `post-${lastIndex}`));
    }
  }

  return parts;
}
